// src/pages/modules/Admin/LogViewer/LogViewer.tsx
import React from 'react'
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import { dbRead } from '../../../../_services/firebase'
import { DB_ACTION_LOG } from '../../../../_config/db_paths'
import { formatDate, toastDanger } from '../../../../_utils'
import type { ActionLog } from '../../../../_types'

const ACTION_BADGE: Record<string, string> = {
  login:           'secondary',
  create:          'success',
  update:          'primary',
  delete:          'danger',
  approve:         'info',
  payout:          'warning',
  adjust_interest: 'warning',
  reset_loan:      'warning',
  bad_debt:        'danger',
  close_account:   'dark',
  payment:         'success',
  pin_set:         'secondary',
  pin_fail:        'danger',
}

const ACTION_LABEL: Record<string, string> = {
  login:           'เข้าสู่ระบบ',
  create:          'สร้างใหม่',
  update:          'แก้ไข',
  delete:          'ลบ',
  approve:         'อนุมัติ',
  payout:          'จ่ายเงิน',
  adjust_interest: 'ปรับดอก',
  reset_loan:      'ล้มยอด',
  bad_debt:        'หนี้เสีย',
  close_account:   'ปิดบัญชี',
  payment:         'ชำระงวด',
  pin_set:         'ตั้ง PIN',
  pin_fail:        'PIN ผิด',
}

export default function LogViewer() {
  const [loading,    setLoading]    = React.useState(true)
  const [logs,       setLogs]       = React.useState<(ActionLog & { id: string })[]>([])
  const [search,     setSearch]     = React.useState('')
  const [actionFilter, setActionFilter] = React.useState('all')
  const [limit,      setLimit]      = React.useState(100)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const raw = await dbRead<Record<string, ActionLog>>(DB_ACTION_LOG)
      if (!raw) { setLogs([]); return }
      const list = Object.entries(raw)
        .map(([id, log]) => ({ ...log, id }))
        .sort((a, b) => b.date - a.date)
      setLogs(list)
    } catch (e: any) {
      toastDanger(e?.message || 'โหลด log ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadData() }, [loadData])

  const allActions = React.useMemo(() => {
    const set = new Set(logs.map((l) => l.action))
    return Array.from(set).sort()
  }, [logs])

  const filtered = logs.filter((l) => {
    const matchSearch = !search ||
      l.uid.toLowerCase().includes(search.toLowerCase()) ||
      l.target.toLowerCase().includes(search.toLowerCase()) ||
      (l.detail || '').toLowerCase().includes(search.toLowerCase())
    const matchAction = actionFilter === 'all' || l.action === actionFilter
    return matchSearch && matchAction
  }).slice(0, limit)

  return (
    <div className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>ประวัติการใช้งาน</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            {logs.length} รายการทั้งหมด / แสดง {filtered.length} รายการ
          </div>
        </div>
        <Button size='sm' variant='outline-secondary' onClick={loadData}>
          <i className='fa-solid fa-rotate-right me-1' />รีเฟรช
        </Button>
      </div>

      {/* Filter */}
      <Row className='g-2 mb-3'>
        <Col xs={12} md={5}>
          <Form.Control
            size='sm'
            placeholder='ค้นหา uid / target / รายละเอียด'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col xs={8} md={4}>
          <Form.Select size='sm' value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value='all'>ทุก Action</option>
            {allActions.map((a) => (
              <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={4} md={2}>
          <Button size='sm' variant='outline-danger' className='w-100'
            onClick={() => { setSearch(''); setActionFilter('all') }}>ล้าง</Button>
        </Col>
        <Col xs={12} md={1}>
          <Form.Select size='sm' value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className='text-center p-4'><Spinner animation='border' variant='warning' /></div>
      )}

      {!loading && filtered.length === 0 && (
        <Alert variant='info' style={{ fontSize: 13 }}>ไม่พบ log</Alert>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <Table size='sm' bordered hover style={{ fontSize: 12, minWidth: 700 }}>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 160 }}>วันที่เวลา</th>
                <th style={{ width: 100 }}>Action</th>
                <th>UID ผู้กระทำ</th>
                <th>Target</th>
                <th>รายละเอียด</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.date)}</td>
                  <td>
                    <Badge
                      bg={ACTION_BADGE[log.action] || 'secondary'}
                      text={['warning', 'light'].includes(ACTION_BADGE[log.action]) ? 'dark' : undefined}
                      style={{ fontSize: 10 }}
                    >
                      {ACTION_LABEL[log.action] || log.action}
                    </Badge>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>{log.uid}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>{log.target}</td>
                  <td style={{ color: '#555' }}>{log.detail || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && logs.length > limit && (
        <div className='text-center mt-3'>
          <Button size='sm' variant='outline-secondary' onClick={() => setLimit((l) => l + 100)}>
            โหลดเพิ่ม
          </Button>
        </div>
      )}
    </div>
  )
}